import { SrOnly } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import { visibleSwatchesCount } from './config';
import {
	ColourContainer,
	StyledCount,
	StyledCountExtraExtraSmall,
	StyledCountExtraLarge,
	StyledCountExtraSmall,
	StyledCountLarge,
	StyledCountMedium,
	StyledSwatch,
} from './styled-colour-swatch';

const { useMemo, useCallback } = React;

const propTypes = {
	swatches: PropTypes.arrayOf(
		PropTypes.shape({
			displayName: PropTypes.string,
			imageSrc: PropTypes.string,
		}),
	),
};
const defaultProps = {
	swatches: [],
};

function ColourSwatch({ swatches }) {
	const swatchSize = useMemo(() => ({ xxs: 'small', m: 'medium', l: 'large' }), []);
	const countSize = useMemo(() => ({ xxs: 'm2', m: 'm1', l: '1' }), []);
	const StyledCountComponents = {
		xxs: StyledCountExtraExtraSmall,
		xs: StyledCountExtraSmall,
		m: StyledCountMedium,
		l: StyledCountLarge,
		1350: StyledCountExtraLarge,
	};

	const renderColoursText = useCallback(
		(device) => `+${swatches.length - visibleSwatchesCount[device].min} colours`,
		[swatches],
	);

	const getOneOrMany = useCallback((swatchesCount) => (swatchesCount > 1 ? 'colours' : 'colour'));

	const getExtraSwatchesText = useCallback(
		(device) => {
			const hiddenCount = swatches.length - visibleSwatchesCount[device].min;

			const colours = swatches
				.map((swatch, index) =>
					index < visibleSwatchesCount[device].min ? swatch?.displayName : null,
				)
				.filter(Boolean);

			return `Available in ${colours.join(', ')} and ${hiddenCount} more colours`;
		},
		[swatches],
	);

	const getSrOnlyText = useCallback(
		(device) => {
			const hasExtraCount = swatches.length > visibleSwatchesCount[device].max;

			if (!hasExtraCount) {
				const colours = swatches.map((swatch) => swatch?.displayName);

				return `Available in ${colours.join(', ')} ${getOneOrMany(colours.length)}`;
			}

			return getExtraSwatchesText(device);
		},
		[swatches],
	);

	const swatchesTotalCount = swatches.length;

	if (swatchesTotalCount === 0) return null;

	return (
		<ColourContainer>
			{swatches?.length &&
				swatches.slice(0, visibleSwatchesCount['1350'].max).map((swatch, index) => {
					const sizeParam = swatch?.imageSrc && '?$20x20_standard$';

					return (
						<StyledSwatch
							key={swatch?.displayName}
							swatchNumber={index + 1}
							swatchesTotalCount={swatchesTotalCount}
							size={swatchSize}
							name={swatch?.displayName}
							imageSrc={sizeParam ? swatch.imageSrc + sizeParam : swatch?.imageSrc}
						/>
					);
				})}
			<StyledCount
				swatchesTotalCount={swatchesTotalCount}
				type="paragraph"
				size={countSize}
				aria-hidden
			>
				{Object.entries(StyledCountComponents).map(([size, StyledComponent]) => (
					<StyledComponent key={size}>{renderColoursText(size)}</StyledComponent>
				))}
			</StyledCount>

			{Object.entries(StyledCountComponents).map(([size, StyledComponent]) => (
				<StyledComponent key={size}>
					<SrOnly>{getSrOnlyText(size)}</SrOnly>
				</StyledComponent>
			))}
		</ColourContainer>
	);
}

ColourSwatch.propTypes = propTypes;
ColourSwatch.defaultProps = defaultProps;

export default ColourSwatch;
